import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import api, { fmtINR } from "@/lib/api";
import { Printer, Eye, Receipt, FileText, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

/**
 * SelectiveBillDialog — pick which OPD line items to print, choose format
 * (A4 / 58 mm / 80 mm) and fire Print or Preview. Each line item shows its
 * category + description + qty + rate + amount so staff can tick e.g. only
 * the consultation, or only 3 of 5 investigations.
 *
 * The receipt is a *partial receipt* on the print page (label + no discount /
 * paid rollup) since it reflects the items being paid right now.
 */
export default function SelectiveBillDialog({ open, onOpenChange, opdId }) {
  const [bill, setBill] = useState(null);
  const [ticked, setTicked] = useState(new Set());
  const [format, setFormat] = useState("thermal58"); // a4 · thermal58 · thermal80
  const [editingId, setEditingId] = useState("");
  const [editVal, setEditVal] = useState("");
  const [editReason, setEditReason] = useState("");

  useEffect(() => {
    if (!open || !opdId) return;
    setBill(null);
    api.get(`/opd/${opdId}/billing`).then((r) => {
      setBill(r.data);
      // Default: tick everything except Discount items (which never print anyway)
      const ids = (r.data.items || []).filter((i) => i.category !== "Discount").map((i) => i.id);
      setTicked(new Set(ids));
    }).catch(() => toast.error("Could not load bill items"));
  }, [open, opdId]);

  // Group items by category for a cleaner UX (Consultation, Investigation, Procedure, Other…)
  const grouped = useMemo(() => {
    if (!bill?.items) return {};
    const g = {};
    for (const it of bill.items) {
      if (it.category === "Discount") continue;
      (g[it.category] = g[it.category] || []).push(it);
    }
    return g;
  }, [bill]);

  const total = useMemo(() => {
    if (!bill?.items) return 0;
    return bill.items.filter((i) => ticked.has(i.id)).reduce((s, i) => s + (i.amount || 0), 0);
  }, [bill, ticked]);

  const toggle = (id) => {
    const n = new Set(ticked);
    n.has(id) ? n.delete(id) : n.add(id);
    setTicked(n);
  };
  const toggleCategory = (cat) => {
    const ids = grouped[cat].map((i) => i.id);
    const allOn = ids.every((id) => ticked.has(id));
    const n = new Set(ticked);
    ids.forEach((id) => allOn ? n.delete(id) : n.add(id));
    setTicked(n);
  };
  const setAll = (on) => {
    if (!bill?.items) return;
    const eligible = bill.items.filter((i) => i.category !== "Discount").map((i) => i.id);
    setTicked(on ? new Set(eligible) : new Set());
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setEditVal(String(it.amount || 0));
    setEditReason("");
  };
  const cancelEdit = () => { setEditingId(""); setEditVal(""); setEditReason(""); };
  const saveEdit = async () => {
    const amt = parseFloat(editVal);
    if (!Number.isFinite(amt) || amt < 0) return toast.error("Enter a valid amount");
    const reason = editReason.trim() || "Price override";
    try {
      await api.put(`/bill-items/${editingId}/amount`, { new_amount: amt, reason });
      toast.success("Price updated");
      const { data } = await api.get(`/opd/${opdId}/billing`);
      setBill(data);
      cancelEdit();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const fire = (preview) => {
    if (ticked.size === 0) return toast.error("Tick at least one line item");
    const ids = [...ticked].join(",");
    const url = `/bill/opd/${opdId}/print?layout=${format}&items=${encodeURIComponent(ids)}${preview ? "&preview=1" : ""}`;
    window.open(url, "_blank");
  };

  const FORMAT_OPTS = [
    { key: "a4",         label: "Full A4",     icon: FileText },
    { key: "thermal58",  label: "58 mm",       icon: Receipt },
    { key: "thermal80",  label: "80 mm",       icon: Receipt },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Selective Receipt · Pick Items to Print</DialogTitle>
        </DialogHeader>
        {!bill ? (
          <div className="p-4 text-sm text-slate-500">Loading bill items…</div>
        ) : (
          <div className="space-y-3">
            {/* Format toggle */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-slate-500 mr-1">Format</span>
              {FORMAT_OPTS.map((f) => {
                const Icon = f.icon;
                const on = format === f.key;
                return (
                  <button key={f.key} type="button" onClick={() => setFormat(f.key)}
                    data-testid={`sbd-fmt-${f.key}`}
                    className={`flex items-center gap-1 text-xs px-2 py-1 border rounded-sm ${on ? "border-blue-600 bg-blue-50 text-blue-700 font-semibold" : "border-slate-300 hover:border-blue-400"}`}>
                    <Icon className="w-3.5 h-3.5"/> {f.label}
                  </button>
                );
              })}
              <div className="ml-auto text-[11px] flex gap-2">
                <button type="button" onClick={() => setAll(true)}  className="text-blue-700 hover:underline" data-testid="sbd-all">All</button>
                <button type="button" onClick={() => setAll(false)} className="text-slate-500 hover:underline" data-testid="sbd-none">None</button>
              </div>
            </div>

            {/* Grouped line items */}
            <div className="max-h-[380px] overflow-y-auto border rounded-sm">
              {Object.keys(grouped).length === 0 && (
                <div className="p-6 text-center text-xs text-slate-500">No billable items on this OPD yet.</div>
              )}
              {Object.entries(grouped).map(([cat, items]) => {
                const allOn = items.every((i) => ticked.has(i.id));
                const someOn = items.some((i) => ticked.has(i.id));
                return (
                  <div key={cat} className="border-b last:border-b-0">
                    <div className="flex items-center gap-2 bg-slate-50 px-2 py-1.5 sticky top-0">
                      <input type="checkbox"
                        data-testid={`sbd-cat-${cat}`}
                        checked={allOn}
                        ref={(el) => { if (el) el.indeterminate = !allOn && someOn; }}
                        onChange={() => toggleCategory(cat)}
                        className="accent-blue-700"/>
                      <Badge variant="outline" className="rounded-sm text-[10px]">{cat}</Badge>
                      <span className="text-[11px] text-slate-500">{items.length} item{items.length > 1 ? "s" : ""}</span>
                    </div>
                    {items.map((it) => {
                      const isEditing = editingId === it.id;
                      return (
                      <div key={it.id} data-testid={`sbd-item-${it.id}`} className={`flex items-center gap-2 text-xs px-2 py-1.5 border-t transition ${ticked.has(it.id) ? "bg-blue-50/50" : "hover:bg-slate-50"}`}>
                        <input type="checkbox" checked={ticked.has(it.id)} onChange={() => toggle(it.id)} className="accent-blue-700"/>
                        <span className="flex-1 truncate">{it.description}</span>
                        {isEditing ? (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <span className="text-[10px] text-slate-500">₹</span>
                            <Input
                              type="number"
                              value={editVal}
                              onChange={(e) => setEditVal(e.target.value)}
                              className="h-7 w-20 text-xs"
                              data-testid={`sbd-item-amt-${it.id}`}
                              autoFocus
                            />
                            <Input
                              value={editReason}
                              onChange={(e) => setEditReason(e.target.value)}
                              placeholder="Reason (e.g. discount)"
                              className="h-7 w-32 text-xs"
                              data-testid={`sbd-item-reason-${it.id}`}
                            />
                            <button type="button" onClick={saveEdit} className="text-teal-700 hover:bg-teal-50 p-1 rounded-sm" title="Save" data-testid={`sbd-item-save-${it.id}`}><Check className="w-3.5 h-3.5"/></button>
                            <button type="button" onClick={cancelEdit} className="text-slate-500 hover:bg-slate-100 p-1 rounded-sm" title="Cancel"><X className="w-3.5 h-3.5"/></button>
                          </div>
                        ) : (
                          <>
                            <span className="text-[10px] text-slate-500 font-mono w-16 text-right">{it.quantity} × {fmtINR(it.rate)}</span>
                            <span className="w-20 text-right font-mono font-medium">{fmtINR(it.amount)}</span>
                            <button type="button" onClick={(e) => { e.stopPropagation(); startEdit(it); }} className="text-blue-700 hover:bg-blue-50 p-1 rounded-sm" title="Edit price / apply discount" data-testid={`sbd-item-edit-${it.id}`}><Pencil className="w-3 h-3"/></button>
                          </>
                        )}
                      </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {/* Total pill */}
            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-sm px-3 py-2">
              <div className="text-xs text-slate-700"><b>{ticked.size}</b> item{ticked.size !== 1 ? "s" : ""} selected</div>
              <div className="font-mono font-semibold text-blue-800">Total: {fmtINR(total)}</div>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sbd-cancel">Close</Button>
          <Button variant="outline" onClick={() => fire(true)} disabled={ticked.size === 0} data-testid="sbd-preview"><Eye className="w-4 h-4 mr-1"/> Preview</Button>
          <Button onClick={() => fire(false)} disabled={ticked.size === 0} className="bg-blue-700 hover:bg-blue-800" data-testid="sbd-print"><Printer className="w-4 h-4 mr-1"/> Print</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
